import * as React from 'react';

import CircularProgress from 'react-md/lib/Progress/CircularProgress';

import { CentrelinkStaticData, ChildcareCentreDetails, EnrolledChild, FamilyDetails, PersonalDetails } from '../Calculation/types';
import { SubsidyCalculationForm } from './SubsidyCalculationForm';

import { ApiRequest } from '../apiRequest';
import DataLoadFailure from './DataLoadFailure';

import * as queryString from "query-string";

interface Props {
  centrelinkApiUrl: string;
  childcareCentreApiUrl: string;
  estimateCompleteUrl: string;
  customerDetailsUrl: string;
};

enum LoadStatus {
  NotStarted,
  InProgress,
  Successful,
  Failed
}

export interface ChildEnrollmentDetails {
  ageInMonths?: number;
  attendHoursPerDay?: number;
  attendSessionsPerWeek: number;
}

interface State {
  enrolledChildrenCount: number;
  enrolledChildren: ChildEnrollmentDetails[];
  familyDetails: FamilyDetails;
  personalDetails: PersonalDetails;
  centrelinkData?: CentrelinkStaticData;
  childcareCentreData?: ChildcareCentreDetails[];
  loadStatus: LoadStatus;
  selectedChildcareCentre?: ChildcareCentreDetails;
}

export class FormContainer extends React.Component<Props, State> {
  private readonly MaximumEnrolledChildren = 5;

  constructor(props: Props) {
    super(props);

    this.state = {
      enrolledChildrenCount: 1,
      enrolledChildren: Array.from(new Array(this.MaximumEnrolledChildren), () => this.createDefaultEnrolledChild()),
      familyDetails: {
        combinedTaxableIncome: 195000,
        subsidyWitholdingPercentage: 0.05,
        knowSubsidyDetails: true
      },
      personalDetails: {
        preferredName: "",
        email: "",
        number: ""
      },
      loadStatus: LoadStatus.NotStarted
    };
  }

  public componentDidMount() {
    this.loadInitialData();
  }

  public render() {
    switch (this.state.loadStatus) {
      case LoadStatus.NotStarted:
      case LoadStatus.InProgress:
        return <CircularProgress id="InitialDataLoad" centered={true}></CircularProgress>;
      case LoadStatus.Successful:
        return <SubsidyCalculationForm
          estimateCompleteUrl={this.props.estimateCompleteUrl}
          customerDetailsUrl={this.props.customerDetailsUrl}
          enrolledChildrenCount={this.state.enrolledChildrenCount}
          enrolledChildren={this.state.enrolledChildren}
          familyDetails={this.state.familyDetails}
          personalDetails={this.state.personalDetails}
          centrelinkData={this.state.centrelinkData!}
          childcareCentreData={this.state.childcareCentreData!}
          selectedChildcareCentre={this.state.selectedChildcareCentre}
          maximumEnrolledChildren={this.MaximumEnrolledChildren}
          onCombinedIncomeUpdated={this.onCombinedIncomeUpdated}
          onKnowSubsidyDetailsUpdated={this.onKnowSubsidyDetailsUpdated}
          onSubsidyPercentageUpdated={this.onSubsidyPercentageUpdated}
          onHoursEligableForSubsidyUpdated={this.onHoursEligableForSubsidyUpdated}
          onSelectedCentreUpdated={this.onSelectedCentreUpdated}
          onRecognisedActivityUpdated={this.onRecognisedActivityUpdated}
          onEnrolledChildrenCountUpdated={this.onEnrolledChildrenCountUpdated}
          onEnrolledChildDetailsUpdated={this.onEnrolledChildDetailsUpdated}
          onNameUpdated={this.onNameUpdated}
          onEmailUpdated={this.onEmailUpdated}
          onNumberUpdated={this.onNumberUpdated}
        ></SubsidyCalculationForm>
      default:
        return <DataLoadFailure></DataLoadFailure>
    }
  }

  private createDefaultEnrolledChild(): ChildEnrollmentDetails {
    return {
      attendSessionsPerWeek: 1, ageInMonths: 0
    }
  }

  private async loadInitialData() {
    this.setState({
      loadStatus: LoadStatus.InProgress
    });

    const api = new ApiRequest();

    const centrelinkFetch = api.get<CentrelinkStaticData>(this.props.centrelinkApiUrl);
    const childcareCentreFetch = api.get<ChildcareCentreDetails[]>(this.props.childcareCentreApiUrl);

    // const hardCodedCentrelinkData: CentrelinkStaticData = { "daycarePricingCaps": { "centreBasedDaycare": { "childrenBelowSchoolAge": 11.77, "schoolAgedChildren": 10.29 }, "outsideSchoolHoursCare": { "childrenBelowSchoolAge": 11.77, "schoolAgedChildren": 10.29 } }, "incomeSubsidyCurve": [{ "combinedAnnualIncome": 0, "subsidyPercentage": 0.85 }, { "combinedAnnualIncome": 66958, "subsidyPercentage": 0.85 }, { "combinedAnnualIncome": 171958, "subsidyPercentage": 0.5 }, { "combinedAnnualIncome": 251247, "subsidyPercentage": 0.5 }, { "combinedAnnualIncome": 341248, "subsidyPercentage": 0.2 }, { "combinedAnnualIncome": 351247, "subsidyPercentage": 0.2 }], "annualSubsidyCapCurve": [{ "combinedAnnualIncome": 186959, "annualSubsidyCap": 10190 }, { "combinedAnnualIncome": 351247, "annualSubsidyCap": 10190 }], "subsidisedHoursEntitlement": { "lowIncomeFamilyIncomeThreshold": 66958, "lowIncomeFamilySubsidisedHoursEntitlement": 24, "activityHoursSubsidisedHoursCurve": [{ "activityHours": 8, "subsidisedHours": 36 }, { "activityHours": 16, "subsidisedHours": 36 }, { "activityHours": 17, "subsidisedHours": 72 }, { "activityHours": 48, "subsidisedHours": 72 }, { "activityHours": 49, "subsidisedHours": 100 }, { "activityHours": 1000, "subsidisedHours": 100 }] } };

    // const hardCodedChildcareCentreData: ChildcareCentreDetails[] = [{ "displayName": "Southport", "state": "", "suburb": "Southport", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 95 }, { "ageOfChildInMonths": 23, "dailyRate": 95 }, { "ageOfChildInMonths": 24, "dailyRate": 95 }, { "ageOfChildInMonths": 35, "dailyRate": 95 }, { "ageOfChildInMonths": 36, "dailyRate": 91 }, { "ageOfChildInMonths": 72, "dailyRate": 91 }], "sortOrder": 60, "id": 1285 }, { "displayName": "Sutherland", "state": "", "suburb": "Sutherland", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 125 }, { "ageOfChildInMonths": 23, "dailyRate": 125 }, { "ageOfChildInMonths": 24, "dailyRate": 125 }, { "ageOfChildInMonths": 35, "dailyRate": 125 }, { "ageOfChildInMonths": 36, "dailyRate": 115 }, { "ageOfChildInMonths": 72, "dailyRate": 115 }], "sortOrder": 50, "id": 1284 }, { "displayName": "Peakhurst", "state": "", "suburb": "Peakhurst", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 112 }, { "ageOfChildInMonths": 23, "dailyRate": 112 }, { "ageOfChildInMonths": 24, "dailyRate": 103 }, { "ageOfChildInMonths": 35, "dailyRate": 103 }, { "ageOfChildInMonths": 36, "dailyRate": 98 }, { "ageOfChildInMonths": 72, "dailyRate": 98 }], "sortOrder": 40, "id": 1283 }, { "displayName": "Brookvale", "state": "", "suburb": "Brookvale", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 130 }, { "ageOfChildInMonths": 23, "dailyRate": 130 }, { "ageOfChildInMonths": 24, "dailyRate": 125 }, { "ageOfChildInMonths": 35, "dailyRate": 125 }, { "ageOfChildInMonths": 36, "dailyRate": 115 }, { "ageOfChildInMonths": 72, "dailyRate": 115 }], "sortOrder": 30, "id": 1282 }, { "displayName": "Belmore, Benaroon Road", "state": "", "suburb": "Belmore", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 115 }, { "ageOfChildInMonths": 23, "dailyRate": 115 }, { "ageOfChildInMonths": 24, "dailyRate": 115 }, { "ageOfChildInMonths": 35, "dailyRate": 115 }, { "ageOfChildInMonths": 36, "dailyRate": 98 }, { "ageOfChildInMonths": 72, "dailyRate": 98 }], "sortOrder": 20, "id": 1281 }, { "displayName": "Belmore, Cleary Avenue", "state": "", "suburb": "Belmore", "pricing": [{ "ageOfChildInMonths": 0, "dailyRate": 105 }, { "ageOfChildInMonths": 23, "dailyRate": 105 }, { "ageOfChildInMonths": 24, "dailyRate": 99 }, { "ageOfChildInMonths": 35, "dailyRate": 99 }, { "ageOfChildInMonths": 36, "dailyRate": 95 }, { "ageOfChildInMonths": 72, "dailyRate": 95 }], "sortOrder": 10, "id": 1279 }];
    // const centrelinkFetch = Promise.resolve(hardCodedCentrelinkData);
    // const childcareCentreFetch = Promise.resolve(hardCodedChildcareCentreData);

    let centrelinkData: CentrelinkStaticData | undefined;
    let childcareCentreData: ChildcareCentreDetails[] | undefined;
    let loadStatus: LoadStatus;

    try {
      [centrelinkData, childcareCentreData] = await Promise.all([centrelinkFetch, childcareCentreFetch]);
      loadStatus = LoadStatus.Successful;
    } catch (e) {
      console.log("Error loading from API:", e);
      loadStatus = LoadStatus.Failed;
    }

    const parsedQueryString = queryString.parse(window.location.search || "");
    const selectedCentreName: string = parsedQueryString["centre"] || "";
    let selectedChildcareCentre
    if (childcareCentreData) {
      selectedChildcareCentre = childcareCentreData.find(c => c.displayName.toUpperCase() === selectedCentreName.toUpperCase());
    }

    this.setState({ loadStatus, centrelinkData, childcareCentreData, selectedChildcareCentre });
  }

  private onRecognisedActivityUpdated = (hours: number) => {
    this.setState({
      familyDetails: {
        ...this.state.familyDetails,
        activityHoursPerFortnight: hours
      }
    });
  };

  private onSelectedCentreUpdated = (value?: ChildcareCentreDetails) => {
    this.setState({ selectedChildcareCentre: value });
  };

  private onCombinedIncomeUpdated = (combinedTaxableIncome: number) => {
    this.setState({
      familyDetails: {
        ...this.state.familyDetails,
        combinedTaxableIncome
      }
    });
  };

  private onKnowSubsidyDetailsUpdated = (knowSubsidyDetails: boolean) => {
    this.setState({
      familyDetails: {
        ...this.state.familyDetails,
        knowSubsidyDetails
      }
    })
  };

  private onSubsidyPercentageUpdated = (subsidyPercentage: number | undefined) => {
    this.setState({
      familyDetails: {
        ...this.state.familyDetails,
        subsidyPercentage,
        hoursEligableForSubsidyPerFortnight: undefined
      }
    })
  };

  private onHoursEligableForSubsidyUpdated = (hours: number | undefined) => {
    this.setState({
      familyDetails: {
        ...this.state.familyDetails,
        hoursEligableForSubsidyPerFortnight: hours
      }
    })
  };

  private onEnrolledChildrenCountUpdated = (enrolledChildrenCount: number) => {
    this.setState({ enrolledChildrenCount });
  };

  private onEnrolledChildDetailsUpdated = (index: number, newDetails: EnrolledChild) => {
    const updatedEnrolledChildren = this.state.enrolledChildren.slice();
    updatedEnrolledChildren.splice(index, 1, newDetails);

    this.setState({ enrolledChildren: updatedEnrolledChildren });
  };

  private onNameUpdated = (name: string) => {
    this.setState({
      personalDetails: {
        ...this.state.personalDetails,
        preferredName: name
      }
    })
  };

  private onEmailUpdated = (email: string) => {
    this.setState({
      personalDetails: {
        ...this.state.personalDetails,
        email
      }
    })
  }

  private onNumberUpdated = (number: string) => {
    this.setState({
      personalDetails: {
        ...this.state.personalDetails,
        number
      }
    })
  }
}

export default SubsidyCalculationForm;
